import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, type ReactNode } from "react";
import type { CompanionFileView } from "@/api/training";
import type { LoadState } from "@/components/dataset";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatDateTime, formatDateTimeSeconds, parseUtcMs } from "@/utils/datetime";
import { downloadSessionCompanionFile } from "../sessionCheckpointDownload";
import {
  SessionArtifactDownloadButton,
  SessionArtifactRow,
  sessionArtifactFileColumns,
  useSessionArtifactDownload,
} from "./sessionArtifactTable";
import styles from "./TrainingSessionDetailPanels.module.css";

type CompanionFileRow = SessionArtifactRow<CompanionFileView>;

// 부속 파일을 남기지 않는 학습이 많다. 그래서 빈 목록은 표가 아니라 안내 한 줄로 표시한다.
// 섹션은 항상 그린다. 파일을 받는 자리가 여기라는 것을 파일이 없는 세션에서도 알 수 있다.
export function CompanionFilesSection({
  sessionId,
  state,
  retry,
}: {
  sessionId: string;
  state: LoadState<CompanionFileView[]>;
  retry: () => void;
}): ReactNode {
  const download = useSessionArtifactDownload((file: CompanionFileView) =>
    downloadSessionCompanionFile(sessionId, file),
  );
  const rows = useMemo(
    () =>
      state.status === "success" ? state.data.map((file) => new SessionArtifactRow(file)) : [],
    [state],
  );
  const source = useMemo(() => new StaticTableSource(rows), [rows]);
  useEffect(() => () => source.dispose(), [source]);

  const columns: readonly DataTableColumn<CompanionFileRow>[] = [
    {
      key: "updatedAt",
      header: "갱신 시각",
      width: "minmax(170px, 1fr)",
      render: ({ file }) => (
        <HatTextTooltip text={formatDateTimeSeconds(parseUtcMs(file.updated_at))}>
          <span>{formatDateTime(parseUtcMs(file.updated_at))}</span>
        </HatTextTooltip>
      ),
    },
    ...sessionArtifactFileColumns<CompanionFileView>(),
    {
      key: "file",
      header: "동작",
      // 부속 파일은 공유 링크 대상이 아니다. 그래서 다운로드 버튼(스피너 포함)만 둔다.
      width: "120px",
      align: "right",
      render: (row) => (
        <span className={styles.checkpointActions}>
          <SessionArtifactDownloadButton
            rowId={row.getRowId()}
            pendingRowId={download.pendingRowId}
            onClick={() => download.start(row)}
          />
        </span>
      ),
    },
  ];

  return (
    <section className={styles.rawSection} aria-label="부속 파일">
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>부속 파일</h3>
        <p className={styles.muted}>
          Checkpoint와 함께 쓰는 파일입니다. dataset_stats.pt는 Training Dataset의 정규화
          통계입니다.
        </p>
      </div>
      {state.status === "idle" || state.status === "loading" ? (
        <LoadingState size="sm">부속 파일을 불러오고 있습니다.</LoadingState>
      ) : state.status === "error" ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: retry }}>
          부속 파일 목록을 불러오지 못했습니다. {state.message}
        </EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState size="sm">이 Session에는 부속 파일이 없습니다.</EmptyState>
      ) : (
        <HatDataTable
          source={source}
          columns={columns}
          ariaLabel="부속 파일 목록"
          showIndex={false}
          minWidth="0px"
        />
      )}
    </section>
  );
}
